/*
* Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
*/
import React, {memo, useCallback, useMemo} from 'react';
import {createStyles, withStyles} from '@material-ui/core/styles';
import MaterialTab from '@material-ui/core/Tab';
import CloseIcon from '@material-ui/icons/Close';
import classnames from 'classnames';
import {GenericObject} from '@ion/core/src/types/entities';
import useExtendStyles from '../../hooks/useExtendStyles';
import Render from '../../render/Render';
import {dlsTabIndicatorBorder, toggleThemeStyle} from '../../theme/helpers';
import {dlsAlt, dlsColor, dlsTypography} from '../../theme/theme';
import useThemeContext from '../../theme/useThemeContext';
import {DLSTheme} from '../../types/types';
import StableButtonLabel from '../buttons/current/StableButtonLabel';
import Tooltip from '../tooltip/Tooltip';
import TabIndicator, {TabIndicatorProps} from './current/TabIndicator';
import TabBadgeView from './TabBadgeView';

// defining props definition for Tab component...
export interface TabProps {
  tab: {tabHeader: string, path?: string, tag?: string, indicator?: TabIndicatorProps},
  onClose?: Function,
  onClick?: Function,
  customTabs?: boolean,
  customStyles?: boolean,
  isCondensedVariant?: boolean,
  overrides?: (theme: DLSTheme) => GenericObject,
  minWidth?: number | string,
  maxWidth?: number | string,
  index?: number,
  selected?: boolean,
  showToolTip?: boolean,
  showIcon?: boolean,
  urgentNotifications?: any,
  allNotifications?: any
};

// defining the style definition for Tab component...
const useStyles = () => {
  const {
    fontFamily,
    fontWeight,
    fontSize,
    lineHeight,
    verticalAlign,
    opacity,
    letterSpacing
  } = dlsTypography('tabs');
  return {
    root : toggleThemeStyle({
      flexGrow : 1,
      color    : dlsColor('black'),
      minWidth : '200px',
      fontFamily,
      fontWeight,
      fontSize,
      lineHeight,
      verticalAlign,
      opacity,
      letterSpacing
    }, {
      fontSize      : '14px !important',
      fontFamily    : 'var(--primary-font-family) !important',
      lineHeight    : '20px !important',
      letterSpacing : '0.5px !important',
      textTransform : 'capitalize'
    }),
    backgroundStyle : toggleThemeStyle({
      backgroundColor : dlsColor('grey6')
    }, {
      backgroundColor : dlsColor('white')
    }),
    altBackgroundStyle : {
      maxWidth        : '50%',
      backgroundColor : dlsColor('white')
    },
    endAdornment : {
      position : 'absolute',
      right    : 0
    },
    tag : {
      backgroundColor : dlsColor('caBlue'),
      borderRadius    : '20px',
      ...dlsAlt('components', 'ionpreviewText', 'new'),
      display         : 'flex',
      padding         : '3px'
    },
    tabLabelContainer : {
      gap            : '8px',
      width          : 'fit-content',
      display        : 'flex',
      alignItems     : 'center',
      justifyContent : 'center'
    }
  };
};

const createTabStyles = (isCurrentTheme: boolean, customStyle: boolean) => {
  if (isCurrentTheme) {
    const {fontWeight: label2FontWeight} = dlsTypography('label2') || {};
    return createStyles({
      root : {
        color             : dlsColor('grey2'),
        padding           : '0',
        height            : '48px',
        minHeight         : '48px',
        boxSizing         : 'border-box',
        fontWeight        : 500,
        borderBottom      : dlsTabIndicatorBorder('resting'),
        '&:focus-visible' : {
          outline : 'none'
        },
        '&:focus-visible $wrapper' : {
          boxShadow : `inset 0 0 0 2px ${dlsColor('skyBlue')}`
        },
        '&:hover' : {
          fontWeight : 700
        },
        '&:hover $wrapper' : {
          backgroundColor : dlsColor('grey6')
        },
        '&:active' : {
          color        : dlsColor('darkTeal'),
          fontWeight   : 700,
          borderBottom : dlsTabIndicatorBorder('selected')
        },
        '&:active $wrapper' : {
          backgroundColor : dlsColor('aquamarine')
        },
        '&$selected' : {
          color        : dlsColor('darkTeal'),
          fontWeight   : label2FontWeight,
          borderBottom : dlsTabIndicatorBorder('selected')
        },
        '&$selected $wrapper' : {
          backgroundColor : dlsColor('paleBlue')
        }
      },
      selected : {},
      wrapper  : {
        height         : '32px',
        display        : 'flex',
        width          : '100% !important',
        padding        : '4px 12px',
        overflow       : 'hidden',
        boxSizing      : 'border-box',
        alignItems     : 'center',
        whiteSpace     : 'nowrap',
        borderRadius   : '8px 8px 0 0',
        textOverflow   : 'ellipsis',
        justifyContent : 'center'
      }
    });
  } else {
    return createStyles({
      root : {
        '&$selected' : {
          color : dlsColor('black')
        }
      },
      selected : {},
      wrapper  : customStyle ? {
        textOverflow : 'ellipsis',
        whiteSpace   : 'nowrap',
        overflow     : 'hidden',
        display      : 'block',
        padding      : '0 12px'
      } : {}
    });
  }
};

// a rule-level toggleThemeStyle(...) override is a thunk - resolve it to its
// style object so it can be merged into the MaterialTab's own rule ...
const resolveRule = (rule?: GenericObject | (() => GenericObject)): GenericObject => (
  typeof rule === 'function' ? rule() : rule
);

// the `materialRoot` / `materialWrapper` keys of the overrides are merged into
// the MaterialTab's own root / wrapper rules (same stylesheet, so they always
// win over the defaults) - they style the MaterialTab's own slots, which the
// overrides' other keys (applied through Tab's own classes) can't reach ...
const createTabStyleComponent = (
  isCurrentTheme: boolean,
  customStyle: boolean,
  overrides?: (theme: DLSTheme) => GenericObject
) => (
  withStyles((theme) => {
    const tabStyles: GenericObject        = createTabStyles(isCurrentTheme, customStyle);
    const {materialRoot, materialWrapper} = overrides?.(theme as DLSTheme) || {};
    return createStyles({
      ...tabStyles,
      root    : {...tabStyles.root, ...resolveRule(materialRoot)},
      wrapper : {...tabStyles.wrapper, ...resolveRule(materialWrapper)}
    });
  })<any>(MaterialTab)
);

const ReportTooltip = function(props) {
  return <Tooltip arrow {...props} />;
};

/**
 * Defines the tab component. This component is reusable and can be custom
 * rendered with props.
 *
 * ```typescript
 *
 *  import {Tab} from '@ion/dls/src/components/tabs';
 *
 *  <Tab tab={tab} onClose={onClose} index={index} />
 *
 * ```
 */
export const Tab = ({
  tab,
  onClose,
  onClick,
  customTabs,
  customStyles,
  allNotifications,
  urgentNotifications,
  isCondensedVariant,
  overrides,
  minWidth,
  maxWidth,
  selected,
  index = 0,
  showToolTip = true,
  showIcon = false
}: TabProps) => {
  const {
    isCurrent : isCurrentTheme
  } = useThemeContext();

  const tabViewStyles = useExtendStyles(useStyles, overrides);
  const classes       = tabViewStyles();

  const TabStyle = useMemo(
    () => createTabStyleComponent(isCurrentTheme, !!customTabs, overrides),
    [isCurrentTheme, customTabs, overrides]
  );

  const renderTag = useCallback((tag: string) => (
    <sup className={classes.tag}>{tag}</sup>
  ), [classes]);

  const onIconClick = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    onClose?.(e, index);
  }, [index, onClose]);

  const onChangeIndex = useCallback((e) => {
    onClick?.(e, index);
  }, [index, onClick]);

  // current theme reserves the bold (hover/pressed/selected) label width up
  // front so state changes never shift the neighbouring tabs ...
  const header = isCurrentTheme ? (
    <StableButtonLabel reserveFontWeight={700}>{tab.tabHeader}</StableButtonLabel>
  ) : tab.tabHeader;

  return (
    <TabStyle className={
      classnames(
        classes.root,
        classes.backgroundStyle,
        isCondensedVariant && classes.altBackgroundStyle
      )}
    key={`${index}-${tab.tabHeader}`}
    classes={{selected: classes.selected}}
    onClick={onChangeIndex}
    selected={selected}
    style={{
      paddingLeft : isCurrentTheme ? 0 : (customStyles && index === 0 ? 0 : '12px'),
      minWidth,
      maxWidth
    }}
    label={showToolTip ? (
      <ReportTooltip title={(
        <div style={{textTransform: 'uppercase', fontSize: 10}}>
          {tab.tabHeader}
        </div>
      )}>
        <span>{header}</span>
      </ReportTooltip>
    ) : (
      <>
        <div className={classes.tabLabelContainer}>
          {header}
          <Render.If condition={isCurrentTheme}>
            <TabBadgeView allNotifications={allNotifications}
              tab={tab?.tab}
              urgentNotifications={urgentNotifications} />
          </Render.If>
          {isCurrentTheme && tab.indicator && <TabIndicator {...tab.indicator} />}
          {tab.tag?.split('').map(() => <>&nbsp;</>)}
        </div>
      </>
    )}
    icon={(showIcon || tab.tag) && (
      <div className={classes.endAdornment}
        onClick={onIconClick}>
        {tab.tag ? renderTag(tab.tag) : <CloseIcon />}
      </div>
    )} />
  );
};

export default memo(Tab);
